import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from 'components/tooltip';
import { Col, Row } from 'react-bootstrap-5';

import GenericList from './generic_list';

import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface Team {
  id: number;
  name: string;
  externalId: string;
  userIds: number[];
  userNames?: string[];
}

interface TeamsListProps {
  allowedTeamIds?: number[] | null;
  companyId?: number;
  selectedTeamIds: number[];
  onAddTeam?: (id: number) => void;
  onRemoveTeam?: (id: number) => void;
}

export default function TeamsList({
  allowedTeamIds = null,
  companyId,
  selectedTeamIds,
  onAddTeam,
  onRemoveTeam
}: TeamsListProps) {
  return (
    <Row>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">
            {allowedTeamIds ? 'Teams Available in Corporate Defaults' : 'All Available Teams'}
          </strong>
        </div>
        {/* Only the pickable side is limited: teams already granted access have to stay removable */}
        <TeamList
          allowedTeamIds={allowedTeamIds}
          companyId={companyId}
          excludeTeamIds={selectedTeamIds || []}
          onAddTeam={onAddTeam}
        />
      </Col>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">Teams with Access</strong>
        </div>
        <TeamList companyId={companyId} includeTeamIds={selectedTeamIds || []} onRemoveTeam={onRemoveTeam}/>
      </Col>
    </Row>
  );
}

interface TeamListProps {
  allowedTeamIds?: number[] | null;
  companyId?: number;
  includeTeamIds?: number[];
  excludeTeamIds?: number[];
  onAddTeam?: (id: number) => void;
  onRemoveTeam?: (id: number) => void;
}

function TeamList({
  allowedTeamIds = null,
  companyId,
  includeTeamIds,
  excludeTeamIds,
  onAddTeam,
  onRemoveTeam
}: TeamListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);

  // Two searches can be in flight at once - the debounce only coalesces the calls it hasn't fired
  // yet - and they don't have to come back in order. Only the newest one is allowed to land, so a
  // slow failure can't put an error over a result that arrived after it.
  const latestRequest = useRef<number>(0);
  const [teams, setTeams] = useState<Team[]>([]);

  const searchTeams = useCallback(
    debounce((
      _searchText: string,
      _companyId?: number,
      _includeTeamIds?: number[],
      _excludeTeamIds?: number[],
      _allowedTeamIds?: number[] | null
    ) => {
      const params: {
        limit?: number, [key: string]: string | number | boolean | null | undefined
      } = { limit: 15 };

      // Spelling the ids out — already granted, or the corporate allow-list —
      // makes the result a bounded set, so it doesn't need capping. Capping it
      // would hide the tail of a corporate list longer than the limit.
      if (_includeTeamIds?.length || _allowedTeamIds?.length) params.limit = undefined;

      if (_searchText) params['q[name_cont]'] = _searchText;
      if (_companyId) params['company_id'] = _companyId;
      let queryString = qs.stringify(params);

      // Never combined with `_allowedTeamIds`: both would land in the same
      // ransack `id_in` array and widen the result instead of narrowing it
      if (_includeTeamIds) _includeTeamIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_allowedTeamIds) _allowedTeamIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_excludeTeamIds) _excludeTeamIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`;
      });

      const request = ++latestRequest.current;

      // Not the corporate route: a company user has no corporate session and would only get a 401
      get(`/company_teams?${queryString}`).then((data: Team[]) => {
        if (request !== latestRequest.current) return;

        setTeams(data);
        setLoading(false);
      }).catch(() => {
        if (request !== latestRequest.current) return;

        // Without this the list keeps saying "Loading..." for as long as the page is open
        setTeams([]);
        setLoading(false);
        setFailed(true);
      });
    }, 400),
    []
  );

  useEffect(() => {
    // Ahead of the early returns: a stale failure would otherwise outlive the request it came from
    setFailed(false);

    // Anything in flight, or still waiting for the debounce to fire, belongs to a state the panel
    // has moved on from: ignore the one, drop the other before it asks with stale arguments
    latestRequest.current += 1;
    searchTeams.cancel();

    if (onRemoveTeam && !includeTeamIds?.length) {
      // Nothing is coming back to clear this, since the response above is ignored
      setLoading(false);
      setTeams([]);
      return;
    }

    // An empty allowed list would make ransack drop the filter and return
    // every team, so don't search at all
    if (allowedTeamIds?.length === 0) {
      setLoading(false);
      setTeams([]);
      return;
    }

    setLoading(true);
    searchTeams(searchText, companyId, includeTeamIds, excludeTeamIds, allowedTeamIds);
  }, [searchText, companyId, includeTeamIds, excludeTeamIds, allowedTeamIds]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    if (failed) return 'Teams couldn\'t be loaded. Reload the page to try again.';

    // if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding teams';

    if (allowedTeamIds?.length === 0) {
      return 'Corporate defaults don\'t include any team from this company';
    }

    return 'No teams Found';
  };

  const userList = ({ userNames }: { userNames?: string[] }) => {
    if (!userNames?.length) return null;

    const text = userNames.join(', ');
    return (
      <Tooltip content={text} dark>
        <div className="text-muted fs-small text-truncate">{text}</div>
      </Tooltip>
    );
  };
  
  return (
    <GenericList
      emptyText={emptyText()}
      items={teams}
      loading={loading}
      renderItemLabel={team => (
        <div>
          <div>{team.name}</div>
          {userList({ userNames: team.userNames })}
        </div>
      )}
      searchText={searchText}
      onAddItem={onAddTeam}
      onRemoveItem={onRemoveTeam}
      onSetSearchText={setSearchText}
    />
  );
}
